export enum DataEntityTypes {
  Account = "account",
  Organization = "organization",
  Member = "member",
  Property = "property",
  Role = "role",
  User = "user",
  Message = "message"
}

export enum UserStatuses {
    Active = 'Active',
    Suspended = 'Suspended',
    Inactive = 'Inactive',
    Deleted = 'Deleted',
    None = ''
}

export enum OrganizationStatuses {
    Active = 'Active',
    Inactive = 'Inactive',
    Deleted = 'Deleted',
    None = ''
}

export enum MemberStatuses {
    Created = 'Created',
    Invited = 'Invited',
    Expired = 'Expired',
    Active = 'Active',
    Suspended = 'Suspended',
    Deactivated = 'Deactivated',
    Deleted = 'Deleted',
    None = ''
}

// Organization role IDs seeded in the Authentication database (most-permissive-role filtering,
// DEV-2544). Keys double as display labels via the enum's reverse mapping (RoleIds[3] === 'Owner').
export enum RoleIds {
    Owner = 3,
    Administrator = 4,
    Member = 5
}

// User-level (non organization-scoped) role names, exactly as seeded in the Core API's rbac.json.
// These are the only roles that appear in an AuthorizationModel's userPermissions.roles — the
// organization-scoped ones (Owner/Administrator/Member) come back under memberPermissions instead.
// Also the values `UserModel.mostPermissiveRole` reports.
export enum PlatformRoles {
    SystemAdministrator = 'System Administrator',
    StaffManager = 'Staff Manager',
    Staff = 'Staff'
}

// Most permissive first. A user may hold several of these roles, and the profile header has a
// single slot, so the first match wins (DEV-2598).
export const PLATFORM_ROLE_PRECEDENCE: PlatformRoles[] = [
    PlatformRoles.SystemAdministrator,
    PlatformRoles.StaffManager,
    PlatformRoles.Staff
];

export enum PropertyStatuses {
    Active = 'Active',
    Inactive = 'Inactive',
    Deleted = 'Deleted'
}

export enum PropertyMemberTypes {
    Owner = 'Owner',
    Tenant = 'Tenant'
}

export enum BillingStatuses {
    Trial = 'Trial',
    Paid = 'Paid',
    Suspended = 'Suspended',
    Demo = 'Demo',
}

export enum PricingTiers {
    Standard = 'Standard',
    Premium = 'Premium',
    WhiteGlove = 'WhiteGlove',
    Custom = 'Custom',
    Trial = 'Trial',
}

export enum AddressTypes {
    Billing = "Billing",
    Mailing = "Mailing",
    Physical = "Physical"
}

export enum InvitationStatusColors {
    Accepted = "#198754",
    Invited = "#0d6efd",
    Expired = "#ffc107",
    Created = "#00b1a2ff",
    Uninvited = "#6c757d",
}

export enum UserInvitationStatuses {
    Accepted = 'Accepted',
    Invited = 'Invited',
    Expired = 'Expired',
    None = ''
}

export enum CountryAlpha2Codes {
    UnitedStatesOfAmerica = "US",
    Canada = "CA",
    Mexico = "MX",
    UnitedKingdom = "GB",
    CostaRica = "CR",
    DominicanRepublic = "DO",
    Bahamas = "BS",
    BritishVirginIslands = "VG",
    CaymanIslands = "KY",
}

// The verification service's verdict on an address, normalized by the API across providers.
export enum AddressDeliverability {
    Deliverable = "Deliverable",
    DeliverableWithIssues = "DeliverableWithIssues",
    Undeliverable = "Undeliverable"
}

// Whether a saved address was checked against the verification service, and what the user chose
// when it was. Set by the API at save time by matching the posted address against a verification.
export enum AddressVerificationStatus {
    Unverified = "Unverified",
    Verified = "Verified",
    UserEntered = "UserEntered"
}

export enum MemberStatusActions {
    Activate = "activate",
    Deactivate = "deactivate",  
    Suspend = "suspend",
    Unsuspend = "unsuspend"
}

export enum UserStatusActions {
    Activate = "activate",
    Deactivate = "deactivate"
}

export enum PropertyStatusActions {
    Activate = "activate",
    Deactivate = "deactivate"
}

export enum ScheduleFrequency {
    Once = 'Once',
    Monthly = 'Monthly',
    Annually = 'Annually',
    Custom = 'Custom'
}

export enum ChartOfAccountCategory {
    Assets = 'Assets',
    AssetsCash = 'AssetsCash',
    Liabilities = 'Liabilities',
    Equity = 'Equity',
    Revenue = 'Revenue',
    Expenses = 'Expenses'
}

export enum BudgetFrequency {
    Yearly = 'Yearly',
    Quarterly = 'Quarterly',
    Monthly = 'Monthly'
}

export enum Currency {
    BahamianDollar = 'BSD',
    CanadianDollar = 'CAD',
    CaymanIslandsDollar = 'KYD',
    CostaRicanColon = 'CRC',
    DominicanPeso = 'DOP',
    MexicanPeso = 'MXN',
    BritishPound = 'GBP',
    UnitedStatesDollar = 'USD'
}

export enum SelectionPrimaryKeys {
    UserId = "userId",
    MemberId = "memberId",
    OrganizationId = "organizationId",
    PropertyId = "propertyId",
    PropertyChargeScheduleId = "propertyChargeScheduleId",
    PropertyChargeId = "propertyChargeId",
    DocumentNodeId = "documentNodeId"
}

export enum ChargeStatus {
    Open = 'Open',
    Late = 'Late',
    Paid = 'Paid',
    PaidLate = 'PaidLate',
    Locked = 'Locked',
    Voided = 'Voided',
    Pending = 'Pending'
}

export enum ExpenseStatus {
    Pending = 'Pending',
    Cleared = 'Cleared',
    Voided = 'Voided'
}

export enum CardBrand {
    Visa = 'visa',
    Mastercard = 'mastercard',
    Amex = 'amex',
    Discover = 'discover',
    Diners = 'diners',
    Jcb = 'jcb',
    UnionPay = 'unionpay',
    Unknown = 'unknown'
}

export enum PaymentMethodType {
    Card = 'Card',
    AchDebit = 'AchDebit'
}

export enum PaymentStatus {
    Pending = 'Pending',
    Succeeded = 'Succeeded',
    Failed = 'Failed',
    Canceled = 'Canceled',
    Refunded = 'Refunded',
    Marked = 'Marked',
    Unmarked = 'Unmarked',
    RefundPending = 'RefundPending',
    Canceling = 'Canceling'
}

export enum ChargeMarkAction {
    Paid = 'Paid',
    Void = 'Void'
}

export enum ManualPaymentType {
    Check = 'Check',
    Cash = 'Cash',
    Card = 'Card',
    ACH = 'ACH',
    Other = 'Other'
}

export enum OrganizationContactType {
    General = 'General',
    Billing = 'Billing'
}

export enum DocumentNodeType {
    Folder = 'Folder',
    File = 'File'
}

// The Document Center page's mutually-exclusive view: navigate folders, search by name, or the audit log.
export enum DocumentViewMode {
    Browse = 'browse',
    Search = 'search',
    Activity = 'activity'
}

// The Document Center's desktop/tablet row/column layout for its listing (DEV-2559) — orthogonal to
// DocumentViewMode (Browse/Search/Activity). Mobile always renders its own single card layout
// regardless of this value.
export enum DocumentViewLayout {
    List = 'list',
    Grid = 'grid'
}

export enum DocumentPermission {
    AdminOnly = 'AdminOnly',
    MemberRead = 'MemberRead',
    Public = 'Public'
}

// High-level file-type category returned by the API for each document (drives the grid's category icon).
export enum FileTypeCategory {
    Pdf = 'PDF',
    Image = 'Image',
    Document = 'Document',
    Spreadsheet = 'Spreadsheet',
    Video = 'Video',
    Archive = 'Archive',
    Other = 'Other'
}

// Availability status of a document in the repository (commit returns Available in the happy path).
export enum DocumentStatus {
    Pending = 'Pending',
    Available = 'Available',
    Quarantined = 'Quarantined'
}

// Client-side lifecycle of a single upload in the page's upload queue/dock.
export enum DocumentUploadStatus {
    Pending = 'Pending',
    Uploading = 'Uploading',
    Done = 'Done',
    Failed = 'Failed'
}

// Latest delivery status of a sent email — mirrors the Messaging API's EmailDeliveryStatus.
export enum EmailDeliveryStatus {
    Sent = 'Sent',
    Delivered = 'Delivered',
    Opened = 'Opened',
    Bounced = 'Bounced',
    SpamReport = 'SpamReport',
    Unsubscribed = 'Unsubscribed',
    Suppressed = 'Suppressed',
    Failed = 'Failed'
}

// Type of a sent email — mirrors the Messaging API's TemplateType. Plain-English display
// labels are registered in EnumUtilities.LABEL_OVERRIDES (e.g. InviteMember -> "Invitation to Join").
export enum EmailType {
    InviteMember = 'InviteMember',
    ResetPassword = 'ResetPassword',
    StaffSetPassword = 'StaffSetPassword',
    InvoiceIssued = 'InvoiceIssued',
    InvoiceDayBeforeDue = 'InvoiceDayBeforeDue',
    InvoiceDueDate = 'InvoiceDueDate',
    InvoiceLate = 'InvoiceLate',
    InvoiceMarkedPaid = 'InvoiceMarkedPaid',
    InvoiceUnmarkedPaid = 'InvoiceUnmarkedPaid',
    InvoiceMarkedVoided = 'InvoiceMarkedVoided',
    InvoiceLateFeeApplied = 'InvoiceLateFeeApplied',
    InvoiceLateFeesWaived = 'InvoiceLateFeesWaived',
    PaymentSucceeded = 'PaymentSucceeded',
    PaymentSucceededMultiple = 'PaymentSucceededMultiple',
    PaymentFailed = 'PaymentFailed',
    PaymentFailedMultiple = 'PaymentFailedMultiple',
    PaymentPending = 'PaymentPending',
    PaymentPendingMultiple = 'PaymentPendingMultiple',
    PaymentFailedForOrg = 'PaymentFailedForOrg',
    PaymentFailedMultipleForOrg = 'PaymentFailedMultipleForOrg',
    PaymentCanceled = 'PaymentCanceled',
    PaymentCanceledMultiple = 'PaymentCanceledMultiple',
    RefundSucceededPayer = 'RefundSucceededPayer',
    RefundSucceededAdmin = 'RefundSucceededAdmin',
    RefundFailedAdmin = 'RefundFailedAdmin',
    BasicEmailBlast = 'BasicEmailBlast',
    SupportTicket = 'SupportTicket'
}
